import {
  readStoredSession, TOKEN_KEY, USER_KEY, useAuthStore, type SessionEndReason,
} from '@/store/auth';
import { queryClient } from './query-client';
import { isTokenExpired, msUntilExpiry } from './jwt';

/**
 * Session lifecycle — everything that decides a sign-in is over.
 *
 *  • Expiry timer: the token carries its own `exp`, so the session ends on
 *    time and the user lands on the login page, rather than on whatever
 *    error the next click produces.
 *  • Tab comes back into view / window regains focus: background tabs have
 *    their timers throttled and a sleeping laptop runs none, so the check is
 *    repeated the moment the user can see the page again.
 *  • Cross-tab sync: every tab shares one localStorage session. Signing in
 *    or out in one tab carries to the rest, so a stale tab can never sign
 *    out the fresh session another tab just created.
 *  • Server rejection: api-client calls `endSession` when the server refuses
 *    the token (revoked, user deactivated, workspace gone) — the server is
 *    the final word; the timer only gets there first.
 *
 * AppShell does the redirect: once the store clears it renders <Navigate>
 * to the login page, carrying the current location so sign-in returns there.
 */

/** End the signed-in session. Idempotent: a page firing ten queries at once
 *  gets ten 401s back, and must end the session once. */
export function endSession(reason: SessionEndReason): void {
  if (!useAuthStore.getState().isAuthenticated) return;
  useAuthStore.getState().clear(reason);
  // Drop cached server data only after React has swapped the shell for the
  // login redirect, so no still-mounted page refetches into an empty cache.
  setTimeout(() => queryClient.clear(), 0);
}

/** End the session now if its token has run out. True when it did. */
export function expireIfStale(): boolean {
  const { token, isAuthenticated } = useAuthStore.getState();
  if (!isAuthenticated || !token || !isTokenExpired(token)) return false;
  endSession('expired');
  return true;
}

/**
 * The server rejected `staleToken`, but another tab may have signed in again
 * since this request left. If localStorage holds a different, live token,
 * switch to it and report true so the caller retries once.
 */
export function adoptNewerSession(staleToken: string): boolean {
  const { token, user } = readStoredSession();
  if (!token || !user || token === staleToken || isTokenExpired(token)) return false;
  adopt(token, user);
  return true;
}

function adopt(token: string, user: NonNullable<ReturnType<typeof readStoredSession>['user']>) {
  const previous = useAuthStore.getState().user;
  // setState, not setSession: the value is already in localStorage, and
  // writing it back would echo a storage event to every other tab.
  useAuthStore.setState({ token, user, isAuthenticated: true, endReason: null });
  // A different person signed in elsewhere — this tab must not keep showing
  // the previous user's data out of the cache.
  if (previous?.id !== user.id) queryClient.clear();
}

function onStorage(e: StorageEvent): void {
  // key === null means localStorage.clear() — treat it like a removal.
  if (e.key !== null && e.key !== TOKEN_KEY && e.key !== USER_KEY) return;
  const stored = readStoredSession();
  const current = useAuthStore.getState();

  if (stored.token && stored.user) {
    if (stored.token !== current.token || stored.user.id !== current.user?.id) {
      adopt(stored.token, stored.user);
    }
    return;
  }
  if (!stored.token && current.isAuthenticated) {
    const expired = !!current.token && isTokenExpired(current.token);
    endSession(expired ? 'expired' : 'signed-out-elsewhere');
  }
}

// setTimeout overflows past ~24.8 days and fires immediately; clamp it.
const MAX_TIMER_MS = 2 ** 31 - 1;
let expiryTimer: ReturnType<typeof setTimeout> | undefined;

function scheduleExpiry(token: string | null): void {
  clearTimeout(expiryTimer);
  if (!token) return;
  const ms = msUntilExpiry(token);
  if (ms === null) return;
  // +1 s so the timer lands just after `exp`, not a hair before it.
  expiryTimer = setTimeout(() => {
    if (!expireIfStale()) scheduleExpiry(useAuthStore.getState().token);
  }, Math.min(Math.max(ms, 0) + 1000, MAX_TIMER_MS));
}

let started = false;

/** Called once at boot, before the app renders. */
export function startSessionWatch(): void {
  if (started || typeof window === 'undefined') return;
  started = true;

  scheduleExpiry(useAuthStore.getState().token);
  useAuthStore.subscribe((s, prev) => {
    if (s.token !== prev.token) scheduleExpiry(s.token);
  });

  window.addEventListener('storage', onStorage);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') expireIfStale();
  });
  window.addEventListener('focus', () => { expireIfStale(); });
}
